import React, { useEffect, useState } from 'react';
import {
  Grid, Card, CardContent, Typography, Box, Chip, Paper, CircularProgress,
  List, ListItem, ListItemText, Divider, Tooltip,
} from '@mui/material';
import FolderOpen from '@mui/icons-material/FolderOpen';
import TrendingUp from '@mui/icons-material/TrendingUp';
import Assignment from '@mui/icons-material/Assignment';
import PrecisionManufacturing from '@mui/icons-material/PrecisionManufacturing';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { projectsAPI, communicationsAPI } from '../services/api';

const STATUS_CONFIG = [
  { key: 'opportunity',       label: 'Oportunidad',        color: 'default' },
  { key: 'conceptual',        label: 'Ing. Conceptual',    color: 'info' },
  { key: 'offer_preparation', label: 'Ing. Basica',        color: 'warning' },
  { key: 'offer_sent',        label: 'Oferta Enviada',     color: 'secondary' },
  { key: 'offer_approved',    label: 'Oferta Aprobada',    color: 'success' },
  { key: 'in_progress',       label: 'Ing. Detalle',       color: 'primary' },
  { key: 'commissioning',     label: 'Ejecucion',          color: 'warning' },
  { key: 'completed',         label: 'Completado',         color: 'success' },
  { key: 'cancelled',         label: 'Cancelado',          color: 'error' },
];

const STATUS_LABEL = Object.fromEntries(STATUS_CONFIG.map((s) => [s.key, s.label]));
const STATUS_COLOR = Object.fromEntries(STATUS_CONFIG.map((s) => [s.key, s.color]));

const PRIORITY_LABEL = { high: 'Alta', medium: 'Media', low: 'Baja' };
const PRIORITY_COLOR = { high: 'error', medium: 'warning', low: 'default' };

const PENDING_STATUS_LABEL = { open: 'Abierto', in_progress: 'En curso', resolved: 'Resuelto' };

function daysOverdue(dueDateStr) {
  const due = new Date(dueDateStr);
  const now = new Date();
  return Math.ceil((now - due) / (1000 * 60 * 60 * 24));
}

export default function Dashboard() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [overdue, setOverdue] = useState([]);

  const loadAll = () => {
    Promise.all([
      projectsAPI.list({ limit: 500 }),
      communicationsAPI.listOverduePending(),
    ])
      .then(([projRes, overdueRes]) => {
        setProjects(projRes.data);
        setOverdue(overdueRes.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadAll();
    const onVisible = () => { if (document.visibilityState === 'visible') loadAll(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  const countByStatus = (keys) => keys.reduce((acc, k) => acc + projects.filter((p) => p.status === k).length, 0);

  const summaryCards = [
    {
      title: 'Oportunidades / Ofertas',
      value: countByStatus(['opportunity', 'conceptual', 'offer_preparation', 'offer_sent', 'offer_approved']),
      icon: <FolderOpen fontSize="large" />,
      color: '#f57c00',
    },
    {
      title: 'En Ejecucion',
      value: countByStatus(['in_progress', 'commissioning']),
      icon: <Assignment fontSize="large" />,
      color: '#c62828',
    },
    {
      title: 'Completados',
      value: countByStatus(['completed']),
      icon: <PrecisionManufacturing fontSize="large" />,
      color: '#2e7d32',
    },
    {
      title: 'Total Proyectos',
      value: projects.length,
      icon: <TrendingUp fontSize="large" />,
      color: '#c62828',
    },
  ];

  const statusCounts = STATUS_CONFIG.map((s) => ({
    ...s,
    count: projects.filter((p) => p.status === s.key).length,
  })).filter((s) => s.count > 0);

  const recentProjects = [...projects]
    .sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at))
    .slice(0, 5);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom fontWeight="bold">
        Dashboard
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        {summaryCards.map((card) => (
          <Grid item xs={12} sm={6} md={3} key={card.title}>
            <Card elevation={2}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography color="textSecondary" variant="body2">{card.title}</Typography>
                    <Typography variant="h4" fontWeight="bold">{card.value}</Typography>
                  </Box>
                  <Box sx={{ color: card.color }}>{card.icon}</Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Overdue pending topics */}
      {overdue.length > 0 && (
        <Paper
          sx={{ p: 3, mb: 3, border: '2px solid #c62828', backgroundColor: '#fff8f8' }}
          elevation={0}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <WarningAmberIcon sx={{ color: '#c62828' }} />
            <Typography variant="h6" sx={{ color: '#c62828' }} fontWeight="bold">
              Temas Pendientes Vencidos
            </Typography>
            <Chip
              label={overdue.length}
              size="small"
              sx={{ backgroundColor: '#c62828', color: '#fff', fontWeight: 'bold' }}
            />
          </Box>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Estos temas superaron su fecha limite y aun no estan resueltos.
          </Typography>
          <List disablePadding>
            {overdue.map((item, idx) => {
              const days = daysOverdue(item.due_date);
              return (
                <React.Fragment key={item.id}>
                  {idx > 0 && <Divider />}
                  <ListItem disablePadding sx={{ py: 1.5, alignItems: 'flex-start' }}>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="body1" fontWeight="medium">
                            {item.subject}
                          </Typography>
                          {item.priority && (
                            <Chip
                              label={PRIORITY_LABEL[item.priority] || item.priority}
                              color={PRIORITY_COLOR[item.priority] || 'default'}
                              size="small"
                            />
                          )}
                          <Chip
                            label={PENDING_STATUS_LABEL[item.pending_status] || item.pending_status}
                            size="small"
                            variant="outlined"
                          />
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 0.5, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                          {(item.project_name || item.project_code) && (
                            <Typography variant="caption" color="textSecondary">
                              Proyecto: <strong>{item.project_code ? `${item.project_code} — ` : ''}{item.project_name}</strong>
                            </Typography>
                          )}
                          {item.assigned_to && (
                            <Typography variant="caption" color="textSecondary">
                              Responsable: <strong>{item.assigned_to}</strong>
                            </Typography>
                          )}
                          <Tooltip title={`Fecha limite: ${new Date(item.due_date).toLocaleDateString('es-ES')}`}>
                            <Typography variant="caption" sx={{ color: '#c62828', fontWeight: 'bold' }}>
                              Vencido hace {days} {days === 1 ? 'dia' : 'dias'}
                            </Typography>
                          </Tooltip>
                        </Box>
                      }
                    />
                  </ListItem>
                </React.Fragment>
              );
            })}
          </List>
        </Paper>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Actividad Reciente</Typography>
            {recentProjects.length === 0 ? (
              <Typography color="textSecondary">No hay proyectos todavia. Crea el primero desde el menu Proyectos.</Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                {recentProjects.map((p) => (
                  <Box
                    key={p.id}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      py: 1,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Box>
                      <Typography variant="body1" fontWeight="medium">{p.name}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        {p.code || p.offer_code || `ID: ${p.id}`}
                      </Typography>
                    </Box>
                    <Chip
                      label={STATUS_LABEL[p.status] || p.status}
                      color={STATUS_COLOR[p.status] || 'default'}
                      size="small"
                    />
                  </Box>
                ))}
              </Box>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Estado de Proyectos</Typography>
            {statusCounts.length === 0 ? (
              <Typography color="textSecondary" variant="body2" sx={{ mt: 2 }}>Sin proyectos registrados.</Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
                {statusCounts.map((s) => (
                  <Box key={s.key} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Chip label={s.label} color={s.color} size="small" />
                    <Typography variant="body2" fontWeight="bold">{s.count}</Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
